import { PORTION_SIZE_MEDIUM, PORTION_SIZE_NORMAL, PORTION_SIZE_SMALL } from "../api/constants";
import type { TopupDiscountConfig, TopupDiscountTier } from '../types';
import { Meal, PriceMealsExtra } from "../types";

/** Round to euro cents (avoids floating-point drift in totals). */
export function roundEuro(value: number): number {
    return Math.round(value * 100) / 100;
}

export function getPrice(portionSize: number, priceSmall: number, priceMedium: number, priceNormal: number) {
    if (portionSize === PORTION_SIZE_SMALL)
        return !!priceSmall && priceSmall > 0 ? priceSmall : !!priceMedium && priceMedium > 0 ? priceMedium : !!priceNormal && priceNormal > 0 ? priceNormal : null;
    if (portionSize === PORTION_SIZE_MEDIUM)
        return !!priceMedium && priceMedium > 0 ? priceMedium : !!priceNormal && priceNormal > 0 ? priceNormal : !!priceSmall && priceSmall > 0 ? priceSmall : null;
    if (portionSize === PORTION_SIZE_NORMAL)
        return !!priceNormal && priceNormal > 0 ? priceNormal : !!priceMedium && priceMedium > 0 ? priceMedium : !!priceSmall && priceSmall > 0 ? priceSmall : null;

    return !!priceNormal && priceNormal > 0 ? priceNormal : !!priceMedium && priceMedium > 0 ? priceMedium : !!priceSmall && priceSmall > 0 ? priceSmall : null;
}

export function getPriceCombo(portionSize: number, meal: Meal, combosData?: { [key: string]: any }, isStaff: boolean = false) {
    const comboPrices = combosData?.prices;
    const preferGlobalComboPrices = comboPrices?.preferGlobalComboPrices ?? false;

    // If preferGlobalComboPrices is enabled, skip meal-specific combo prices and use global prices
    if (!preferGlobalComboPrices && meal) {
        if (isStaff && meal?.priceComboStaff) {
            return meal.priceComboStaff;
        }
        if (portionSize === PORTION_SIZE_SMALL && meal?.priceComboSmall)
            return meal.priceComboSmall;
        else if (portionSize === PORTION_SIZE_MEDIUM && meal?.priceComboMedium)
            return meal.priceComboMedium;
        else if (portionSize === PORTION_SIZE_NORMAL && meal?.priceComboLarge)
            return meal.priceComboLarge;
    }

    // Use global combo prices (either because preferGlobalComboPrices is enabled or no meal-specific prices)
    if (comboPrices) {
        // For staff, use staff price if available, otherwise fall back to portion-based pricing
        if (isStaff && comboPrices.priceStaff) {
            return comboPrices.priceStaff;
        }

        switch (portionSize) {
            case PORTION_SIZE_SMALL:
                return comboPrices.priceSmall;
            case PORTION_SIZE_MEDIUM:
                return comboPrices.priceMedium;
            case PORTION_SIZE_NORMAL:
                return comboPrices.priceLarge;
        }
    }
    return meal.priceComboLarge ?? meal.price;
}

export function getPriceSide(portionSize: number, pricesExtra?: PriceMealsExtra | null) {
    if (portionSize === PORTION_SIZE_SMALL)
        return !!pricesExtra?.priceSideSmall ? pricesExtra.priceSideSmall : 0;
    if (portionSize === PORTION_SIZE_MEDIUM)
        return !!pricesExtra?.priceSideMedium ? pricesExtra.priceSideMedium : 0;
    if (portionSize === PORTION_SIZE_NORMAL)
        return !!pricesExtra?.priceSideNormal ? pricesExtra.priceSideNormal : 0;
    return 0;
}

export function getPriceDessert(portionSize: number, pricesExtra?: PriceMealsExtra | null) {
    if (portionSize === PORTION_SIZE_SMALL)
        return !!pricesExtra?.priceDessertSmall ? pricesExtra.priceDessertSmall : 0;
    if (portionSize === PORTION_SIZE_MEDIUM)
        return !!pricesExtra?.priceDessertMedium ? pricesExtra.priceDessertMedium : 0;
    if (portionSize === PORTION_SIZE_NORMAL)
        return !!pricesExtra?.priceDessertNormal ? pricesExtra.priceDessertNormal : 0;
    return 0;
}

export function getPersonnelPrice(price: number, staffDiscount: number) {
    const personnelPrice = price * (1 - staffDiscount / 100);
    return roundToNearest10Cents(personnelPrice);
}

export function calculateTopupDiscount(amount: number, config: TopupDiscountConfig): { discountPercentage: number; tier: TopupDiscountTier | null } {
    if (!config.enabled || !config.tiers || config.tiers.length === 0) {
        return { discountPercentage: 0, tier: null };
    }

    // Sort tiers by minAmount to ensure proper order
    const sortedTiers = [...config.tiers].sort((a, b) => a.minAmount - b.minAmount);

    // Find the applicable tier
    const applicableTier = sortedTiers.find(tier => {
        const meetsMin = amount >= tier.minAmount;
        const meetsMax = tier.maxAmount === undefined || amount <= tier.maxAmount;
        return meetsMin && meetsMax;
    });

    if (!applicableTier) {
        return { discountPercentage: 0, tier: null };
    }

    return {
        discountPercentage: applicableTier.discountPercentage,
        tier: applicableTier
    };
}

// Helper function to round to nearest 0.05€ (5 cents)
function roundToNearest5Cents(price: number): number {
    return Math.round(price * 20) / 20;
}

// Helper function to round to nearest 0.10€ (10 cents)
export function roundToNearest10Cents(price: number): number {
    return Math.round(price * 10) / 10;
}

// Helper function to round to nearest 0 or 5 (no decimal points)
export function roundToNearest0Or5(price: number): number {
    return Math.round(price / 5) * 5;
}

export function applyDiscountToPrice(originalPrice: number, discountPercentage: number): number {
    if (discountPercentage <= 0) {
        return originalPrice;
    }

    const discountAmount = (originalPrice * discountPercentage) / 100;
    return roundToNearest5Cents(originalPrice - discountAmount);
}

// Wrapper functions that apply top-up discount
export function getPriceWithDiscount(portionSize: number, priceSmall: number, priceMedium: number, priceNormal: number, discountPercentage: number = 0) {
    const basePrice = getPrice(portionSize, priceSmall, priceMedium, priceNormal);
    if (basePrice === null) return null;
    return applyDiscountToPrice(basePrice, discountPercentage);
}

export function getPriceComboWithDiscount(portionSize: number, meal: Meal, combosData?: { [key: string]: any }, discountPercentage: number = 0, isStaff: boolean = false) {
    const basePrice = getPriceCombo(portionSize, meal, combosData, isStaff);
    if (basePrice === null) return null;
    return applyDiscountToPrice(basePrice, discountPercentage);
}

export function getPriceSideWithDiscount(portionSize: number, pricesExtra: PriceMealsExtra, discountPercentage: number = 0) {
    const basePrice = getPriceSide(portionSize, pricesExtra);
    return applyDiscountToPrice(basePrice, discountPercentage);
}

export function getPriceDessertWithDiscount(portionSize: number, pricesExtra: PriceMealsExtra, discountPercentage: number = 0) {
    const basePrice = getPriceDessert(portionSize, pricesExtra);
    return applyDiscountToPrice(basePrice, discountPercentage);
}

/** Apply sibling discount for students; staff do not get this discount here */
export function priceWithSiblingDiscount(basePrice: number, isStaff: boolean, discountSiblingsPct: number, isSibling: boolean): number {
    if (isStaff) return basePrice;
    if (!isSibling || !discountSiblingsPct) return basePrice;
    return roundToNearest0Or5(basePrice * (1 - discountSiblingsPct / 100));
}

// Extra price is the price of the meal to change to - (the price of the package meal * (1 - discount) ) 
export function getPackageExchangeMealPrice(recipientIsStudent: boolean, isStaff: boolean, menu1Price: number, meal: Meal, discount: number, staffDiscount: number, quantity: number, chargePackageExchangePrice: boolean = true) {
    if (!meal || meal?.menuType === 1)
        return 0;

    // If charging is disabled, return 0 (free exchange)
    if (!chargePackageExchangePrice) {
        return 0;
    }

    const isSetMenu = meal.courseType === 'main';
    let changePrice = (recipientIsStudent && !!meal.priceSmall) ? meal.priceSmall : isStaff ? getPersonnelPrice(meal.price, staffDiscount) : meal.price;

    if (!isSetMenu)
        changePrice += (!!meal.sideDish ? 0.5 : 0) + (recipientIsStudent ? 0.5 : 0); // 0.5 for side and 0.5 for dessert

    quantity = 1; // keep this for now. Changes are always only for one meal, even if quantity is two.
    const newPrice = (Math.ceil(Math.max(0, changePrice - menu1Price * (1 - discount / 100)) * 10) / 10) * quantity;
    return newPrice > 0 ? newPrice : 0;
} 